import { DateTime } from 'luxon'
import { useDrag } from 'solid-gesture'
import { type Component, createMemo, createSignal, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { Transition } from 'solid-transition-group'
import { twMerge } from 'tailwind-merge'
import type { DateInputFormatType, DateInputPropsType, DateInputTimeModeType, DateInputViewModeType } from '~/types/DateInput'
import { getGsap, prefersReducedMotion } from '~/utils/gsap'
import Button from './Button'
import { useFormContext } from './Form'
import Input from './Input'
import Modal from './Modal'

const weekDays = ['D', 'L', 'Ma', 'Mi', 'J', 'V', 'S']
const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
const formats: Record<DateInputFormatType, string> = {
  year: 'yyyy',
  month: 'yyyy-MM',
  week: 'kkkk-WW',
  date: 'yyyy-MM-dd',
  time: 'HH:mm',
  datetime: 'yyyy-MM-dd HH:mm'
}

const DateInput: Component<DateInputPropsType> = props => {
  const [local, others] = splitProps(props, ['mode', 'format', 'onChange', 'value', 'placeholder', 'disabled', 'min', 'max', 'disabledDates', 'enabledDates', 'class'])
  const [isOpen, setIsOpen] = createSignal(false)
  const [view, setView] = createSignal<DateInputViewModeType>('calendar')
  const [timeMode, setTimeMode] = createSignal<DateInputTimeModeType>('hour')
  const [selectedDate, setSelectedDate] = createSignal(local.value ? DateTime.fromISO(local.value).setLocale('es') : DateTime.now().setLocale('es'))
  const [tempDate, setTempDate] = createSignal(selectedDate())
  const [isPM, setIsPM] = createSignal(selectedDate().hour >= 12)
  const [value, setValue] = createSignal(props.value)
  const yearsPerPage = 18
  const [yearPageStart, setYearPageStart] = createSignal(Math.floor(selectedDate().year / yearsPerPage) * yearsPerPage)

  let clockRef: HTMLDivElement | undefined

  const hourClasses = createMemo(() => twMerge('cursor-pointer', timeMode() === 'hour' ? 'text-primary-600 dark:text-white' : 'text-slate-400'))

  const minuteClasses = createMemo(() => twMerge('cursor-pointer', timeMode() === 'minute' ? 'text-primary-600 dark:text-white' : 'text-slate-400'))
  const getValueFromAngle = (angle: number) => {
    const totalValues = timeMode() === 'hour' ? 12 : 60
    const adjustedAngle = (angle + 90) % 360
    const value = Math.round((adjustedAngle / 360) * totalValues)
    return ((value % totalValues) + totalValues) % totalValues
  }

  const bindDrag = useDrag(
    ({ xy: [x, y], pressed }: { xy: [number, number]; pressed: boolean }) => {
      if (!clockRef) return
      const rect = clockRef.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const angle = Math.atan2(y - centerY, x - centerX) * (180 / Math.PI)
      const value = getValueFromAngle(angle)
      updateTime(value, pressed)
    },
    {
      filterTaps: true,
      threshold: 5,
      rubberband: true,
      bounds: {
        left: -125,
        right: 125,
        top: -125,
        bottom: 125
      }
    }
  )

  const formCtx = useFormContext()

  onMount(() => {
    if (local.mode === 'year-month') setView('year')

    /**
     * El `<Input>` de dentro es de sólo lectura y su valor lo manda esta señal,
     * así que escribirle el DOM —que es lo que hace el `<Form>` con los valores
     * iniciales o con `setValue`— no cambiaba nada: al siguiente render se
     * volvía a pintar el valor viejo.
     */
    const name = others.name ? String(others.name) : undefined
    if (!name || !formCtx) return

    formCtx.registerField(name, {
      getValue: () => value() ?? '',
      setValue: next => {
        const text = next === undefined || next === null ? '' : String(next)
        setValue(text)
        if (text) setSelectedDate(DateTime.fromISO(text).setLocale('es'))
      }
    })

    onCleanup(() => formCtx.unregisterField(name))
  })

  const handleOpenModal = () =>
    !local.disabled && (setIsOpen(true), setView(local.mode === 'year-month' ? 'year' : local.mode === 'time' ? 'time' : 'calendar'), local.mode === 'year-month' && setTempDate(selectedDate()))

  const updateTime = (value: number, pressed: boolean = false) => {
    if (timeMode() === 'hour') {
      const adjustedHour = isPM() ? (value === 12 ? 12 : value + 12) : value === 12 ? 0 : value
      setSelectedDate(prev => prev.set({ hour: adjustedHour }))
      if (!pressed) setTimeMode('minute')
    } else {
      setSelectedDate(prev => prev.set({ minute: value }))
    }
  }

  const formatValue = (date: DateTime): string => date.toFormat(formats[local.format ?? 'datetime'] ?? 'MMM dd, yyyy')
  const getDaysInMonth = () => {
    const date = tempDate()
    const daysInMonth = date.daysInMonth || 31
    const firstDay = date.startOf('month').weekday % 7
    const days = []
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = date.set({ day: i })
      days.push({
        day: i,
        disabled: isDateDisabled(currentDate) || !isDateEnabled(currentDate),
        isToday: currentDate.hasSame(DateTime.now(), 'day'),
        isSelected: currentDate.hasSame(selectedDate(), 'day')
      })
    }

    const prevMonth = date.minus({ months: 1 })
    const prevMonthDays = prevMonth.daysInMonth || 31
    for (let i = 0; i < firstDay; i++) {
      days.unshift({
        day: prevMonthDays - firstDay + i + 1,
        disabled: true,
        isPrevMonth: true,
        isToday: false,
        isSelected: false
      })
    }

    const remainingDays = 42 - days.length
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        disabled: true,
        isNextMonth: true,
        isToday: false,
        isSelected: false
      })
    }

    return days
  }

  const isDateDisabled = (date: DateTime) => {
    if (local.min && date < DateTime.fromISO(local.min)) return true
    if (local.max && date > DateTime.fromISO(local.max)) return true

    const d = local.disabledDates
    if (!d) return false
    if (d.dates?.includes(date.toISODate() ?? '')) return true
    if (d.days?.includes(date.weekday)) return true
    if (d.weeks?.includes(date.weekNumber)) return true
    if (d.months?.includes(date.month)) return true
    if (d.years?.includes(date.year)) return true
    if (d.ranges?.some(r => date >= DateTime.fromISO(r.start) && date <= DateTime.fromISO(r.end))) return true
    return false
  }

  const isDateEnabled = (date: DateTime) => {
    const e = local.enabledDates
    if (!e) return true
    if (e.dates?.length && !e.dates.includes(date.toISODate() ?? '')) return false
    if (e.days?.length && !e.days.includes(date.weekday)) return false
    if (e.weeks?.length && !e.weeks.includes(date.weekNumber)) return false
    if (e.months?.length && !e.months.includes(date.month)) return false
    if (e.years?.length && !e.years.includes(date.year)) return false
    if (e.ranges?.some(r => date >= DateTime.fromISO(r.start) && date <= DateTime.fromISO(r.end))) return true
    return true
  }

  const handlerOnchange = (fecha: DateTime) => {
    const _formatos = Object.assign({}, formats, { datetime: 'yyyy-MM-dd hh:mm a', time: 'hh:mm a' })
    if (local.format === undefined) {
      local.format = ({
        'year-month': 'month',
        date: 'date',
        time: 'time',
        year: 'year',
        datetime: 'datetime'
      }[local.mode] || 'datetime') as DateInputFormatType
    }
    setValue(fecha.toFormat(_formatos[local.format]))
    local.onChange?.(formatValue(fecha))
  }

  const renderClock = () => {
    const numbers = timeMode() === 'hour' ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] : [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]

    const getPosition = (number: number) => {
      const angle = ((timeMode() === 'hour' ? number % 12 : number / 5) / 12) * 360 - 90
      const radian = (angle * Math.PI) / 180
      const radius = 100
      return {
        x: Math.cos(radian) * radius + 125,
        y: Math.sin(radian) * radius + 125
      }
    }

    const currentValue = timeMode() === 'hour' ? selectedDate().hour % 12 || 12 : selectedDate().minute

    return (
      <div
        ref={clockRef}
        {...bindDrag()}
        class={twMerge('relative w-62.5 h-62.5 mx-auto my-2 touch-none select-none')}
        style={{
          'touch-action': 'none',
          '-webkit-tap-highlight-color': 'transparent',
          '-webkit-user-select': 'none',
          'user-select': 'none'
        }}
      >
        <div class="absolute inset-0 rounded-full bg-surface-muted" />
        {numbers.map(number => {
          const pos = getPosition(number)
          const isSelected = timeMode() === 'hour' ? (selectedDate().hour % 12 || 12) === number : selectedDate().minute === number

          const numberClasses = createMemo(() =>
            twMerge(
              'absolute w-12 h-12 font-semibold z-10 rounded-full flex items-center justify-center cursor-pointer select-none',
              isSelected ? 'bg-primary-500 text-white' : 'text-foreground hover:text-primary-500'
            )
          )

          return (
            <div
              class={numberClasses()}
              style={{
                transform: `translate(-50%, -50%)`,
                left: `${pos.x}px`,
                top: `${pos.y}px`,
                'touch-action': 'none'
              }}
              onClick={() => updateTime(number)}
            >
              {timeMode() === 'hour' ? number : number.toString().padStart(2, '0')}
            </div>
          )
        })}

        <div
          class="absolute w-0.5 bg-primary-500"
          style={{
            height: '100px',
            'transform-origin': 'bottom center',
            transform: `translateX(-50%) rotate(${(currentValue / (timeMode() === 'hour' ? 12 : 60)) * 360}deg)`,
            left: '50%',
            top: '25px'
          }}
        />
        <div
          class="absolute w-8 h-8 rounded-full bg-primary-500"
          style={{
            transform: `translate(-50%, -50%)`,
            left: `${getPosition(currentValue).x}px`,
            top: `${getPosition(currentValue).y}px`
          }}
        />
        <div class="absolute w-3 h-3 rounded-full bg-primary-500" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} />
      </div>
    )
  }

  return (
    <>
      <Input
        {...others}
        value={value()}
        readOnly
        disabled={local.disabled}
        class="cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 outline-none"
        onClick={handleOpenModal}
        endIcon={
          local.mode === 'time'
            ? 'material-symbols-light:schedule-outline'
            : local.mode === 'year-month'
              ? 'material-symbols-light:calendar-month-outline'
              : 'material-symbols-light:calendar-month-outline'
        }
      />

      <Modal isOpen={isOpen()} onClose={() => setIsOpen(false)} size="md" class="bg-surface-muted overflow-hidden">
        <div class="flex flex-col p-4">
          <div class="p-4 mb-6 pb-0">
            <div class="flex items-baseline gap-2 justify-center">
              <div class="text-3xl font-semibold text-foreground capitalize">
                {local.mode !== 'time' && selectedDate().toFormat({ year: 'yyyy', 'year-month': 'MMMM yyyy', date: 'MMM dd', datetime: 'MMM dd, yyyy' }[local.mode] || 'MMM dd, yyyy')}
              </div>
            </div>

            {local.mode === 'datetime' && (
              <div class="flex w-full mb-4">
                <div class="grid grid-cols-2 gap-2 w-full pt-4">
                  <Button
                    variant="text"
                    startIcon={'material-symbols-light:calendar-month-outline'}
                    class="text-nowrap"
                    size="sm"
                    color={view() === 'calendar' ? 'primary' : 'default'}
                    onClick={() => setView('calendar')}
                  >
                    {selectedDate().toFormat('MMM dd, yyyy')}
                  </Button>

                  <Button
                    startIcon={'material-symbols-light:schedule-outline'}
                    class="text-nowrap"
                    variant="text"
                    size="sm"
                    color={view() === 'time' ? 'primary' : 'default'}
                    onClick={() => setView('time')}
                  >
                    {selectedDate().toFormat('hh:mm a')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Transition
            mode="outin"
            onEnter={(el: Element, done: () => void) => {
              const g = getGsap()
              if (!g || prefersReducedMotion()) {
                done()
                return
              }
              g.fromTo(el, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.2, ease: 'power2.out', onComplete: done })
            }}
            onExit={(el: Element, done: () => void) => {
              const g = getGsap()
              if (!g || prefersReducedMotion()) {
                done()
                return
              }
              g.to(el, { opacity: 0, y: -20, duration: 0.15, ease: 'power2.in', onComplete: done })
            }}
          >
            <Show when={view() === 'time' || local.mode === 'time'}>
              <div class="flex flex-col">
                <div class="flex justify-center items-center mb-4 gap-3">
                  <div class="flex justify-center text-5xl font-medium tracking-wider select-none">
                    <span class={hourClasses()} onClick={() => setTimeMode('hour')}>
                      {(selectedDate().hour % 12 || 12).toString().padStart(2, '0')}
                    </span>
                    <span class="text-foreground mx-1">:</span>
                    <span class={minuteClasses()} onClick={() => setTimeMode('minute')}>
                      {selectedDate().minute.toString().padStart(2, '0')}
                    </span>
                  </div>
                  <div class="flex gap-1">
                    <Button
                      size="md"
                      variant={!isPM() ? 'soft' : 'text'}
                      color={!isPM() ? 'primary' : 'secondary'}
                      onClick={() => {
                        setIsPM(false)
                        setSelectedDate(prev => prev.set({ hour: prev.hour % 12 }))
                      }}
                    >
                      AM
                    </Button>
                    <Button
                      variant={isPM() ? 'soft' : 'text'}
                      color={isPM() ? 'primary' : 'secondary'}
                      onClick={() => {
                        setIsPM(true)
                        setSelectedDate(prev => prev.set({ hour: (prev.hour % 12) + 12 }))
                      }}
                    >
                      PM
                    </Button>
                  </div>
                </div>

                {renderClock()}
              </div>
            </Show>
            <Show when={view() !== 'time' && local.mode !== 'time'}>
              <div class="flex flex-col">
                <Show when={!['year', 'year-month'].includes(local.mode)}>
                  <div class="flex items-center justify-between mb-6">
                    <Button variant="soft" color="default" onClick={() => setView('year')} endIcon={'material-symbols-light:expand-more'}>
                      {tempDate().toFormat('MMMM yyyy')}
                    </Button>
                    <div class="flex gap-1">
                      <Button variant="text" onClick={() => setTempDate(prev => prev.minus({ months: 1 }))} icon={'material-symbols-light:chevron-left'} iconSize={28} />
                      <Button variant="text" onClick={() => setTempDate(prev => prev.plus({ months: 1 }))} icon={'material-symbols-light:chevron-right'} iconSize={28} />
                    </div>
                  </div>
                </Show>

                <Show
                  when={view() === 'calendar' && !['year', 'year-month'].includes(local.mode)}
                  fallback={
                    <Show
                      when={view() === 'month'}
                      fallback={
                        <div>
                          <div class="mb-3 flex items-center justify-between">
                            <div class="flex gap-2">
                              <Button variant="text" size="sm" icon={'material-symbols-light:chevron-left'} onClick={() => setYearPageStart(prev => prev - yearsPerPage)} />
                              <Button variant="text" size="sm" icon={'material-symbols-light:chevron-right'} onClick={() => setYearPageStart(prev => prev + yearsPerPage)} />
                            </div>
                            <div class="text-sm text-foreground">
                              {yearPageStart()} — {yearPageStart() + yearsPerPage - 1}
                            </div>
                          </div>

                          <div class="grid grid-cols-3 gap-2 max-h-75 overflow-y-auto">
                            {Array.from({ length: yearsPerPage }, (_, i) => yearPageStart() + i).map(year => (
                              <Button
                                variant={tempDate().year === year ? 'contained' : 'text'}
                                color={tempDate().year === year ? 'primary' : 'default'}
                                onClick={() => {
                                  const newDate = tempDate().set({ year })
                                  setTempDate(newDate)
                                  setSelectedDate(newDate)
                                  if (local.mode === 'year') {
                                    handlerOnchange(newDate)
                                    setIsOpen(false)
                                  } else {
                                    setView(local.mode === 'year-month' ? 'month' : 'calendar')
                                  }
                                }}
                              >
                                {year}
                              </Button>
                            ))}
                          </div>
                        </div>
                      }
                    >
                      <div class="grid grid-cols-3 gap-2">
                        {months.map((month, index) => (
                          <Button
                            variant={tempDate().month === index + 1 ? 'contained' : 'text'}
                            color={tempDate().month === index + 1 ? 'primary' : 'default'}
                            onClick={() => {
                              const newDate = tempDate().set({ month: index + 1 })
                              setTempDate(newDate)
                              setSelectedDate(newDate)
                              if (local.mode === 'year-month') {
                                handlerOnchange(newDate)
                              } else {
                                setView('calendar')
                              }
                            }}
                          >
                            {month}
                          </Button>
                        ))}
                      </div>
                    </Show>
                  }
                >
                  <div class="grid grid-cols-7 gap-1">
                    {weekDays.map(day => (
                      <div class="text-center text-sm text-primary mb-2">{day}</div>
                    ))}
                    {getDaysInMonth().map(({ day, disabled, isToday, isSelected }) => (
                      <Button
                        variant={isSelected ? 'contained' : isToday ? 'outlined' : 'text'}
                        disabled={disabled}
                        color={isSelected || isToday ? 'primary' : 'default'}
                        onClick={() => {
                          if (disabled) return
                          const newDate = tempDate().set({ day })
                          setSelectedDate(newDate)
                          if (local.mode === 'date') {
                            handlerOnchange(newDate)
                            setIsOpen(false)
                          } else if (local.mode === 'datetime') {
                            setView('time')
                          }
                        }}
                        class={twMerge(isSelected ? '' : 'hover:bg-slate-200 dark:hover:bg-slate-700')}
                      >
                        {day}
                      </Button>
                    ))}
                  </div>
                </Show>
              </div>
            </Show>
          </Transition>

          <div class="flex justify-between mt-4">
            <Button variant="text" color="secondary" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                handlerOnchange(selectedDate())
                setIsOpen(false)
              }}
            >
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default DateInput
